// @ts-nocheck
// Location: src/components/admin/EngagementPodsAdmin.tsx
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Id, Doc } from '@/convex/_generated/dataModel';
import { useAuth } from '@/hooks/useAuth';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from '@/components/ui/dialog';
import { useConfetti } from '@/hooks/useConfetti';
import {
	canonicalPodPlatform,
	getDefaultPodPlatform,
	getPodModeCapability,
	getPodPlatformIcon,
	getPodPlatformLabel,
	getPodPlatformOptions,
	isPodModeSupported,
	Platform,
	POD_PLATFORM_ORDER,
	Pod,
	PodMode,
	PodStatus as PodStatusType,
} from '@/types/pods';
import { ProfileGroup } from '@/types/profileGroups';
import { PodStatus } from '@/components/pods/PodStatus';
import { truncateKey } from '@/utils/truncateKey';
import { cn } from '@/lib/utils';
import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PodExpandedView } from './pod-expanded/PodExpandedView';
import { validatePodMember } from '@/utils/validation';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelectedPodStore } from '@/stores/selectedPodStore';
import { MiniPodStatus } from '@/components/pods/MiniPodStatus';
import { isBlockedProfile } from '@/utils/profileValidation';
import { PageLoader, LOADER_MESSAGES } from '@/components/ui/loader';
import { SlideSelector } from '@/components/ui/slide-selector';
import { ScheduleStatusBadge } from '@/components/admin/ScheduleStatusBadge';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';

// Generate a virtual pod ID by creating a string unique to the profile and platform
const generateVirtualPodId = (
	profileId: Id<'profiles'>,
	platform: string
): Id<'engagementPods'> => {
	// Create a deterministic string based on profile ID and platform
	const idString = `virtual_pod_${profileId}_${platform}`;
	// Cast it to the required type - this is a virtual ID that won't be used for database operations
	return idString as unknown as Id<'engagementPods'>;
};

// Added LinkStatusValue definition
type LinkStatusValue = { platform: string; status: string; [key: string]: any };

interface PodMember {
	email: string;
	socialUsername: string;
	uploadPostProfileKey: string;
	profileId?: Id<'profiles'> | null;
}

// Define extended pod type to include virtual pods
interface VirtualBroadcastPod {
	_id: Id<'engagementPods'>;
	name: string;
	platform: string;
	mode: 'broadcast';
	userId: Id<'users'>;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
	members: PodMember[];
	isVirtualPod: boolean;
	lastStatus?: {
		status: string;
		message: string;
		timestamp: string;
	};
}

type Profile = {
	_id: Id<'profiles'>;
	email: string;
	profileTitle: string;
	profileKey: string;
	platforms?: {
		platform: string;
		username: string;
		profileUrl: string;
		displayName: string;
	}[];
	isActive: boolean;
	createdAt: string;
	lastUpdated?: string; // Add the lastUpdated property
	linkStatus?: {
		status: string;
		platform: string;
	};
};

// Define type for user pod data
type UserPodData = {
	name: string;
	email: string;
	pods: any[];
};

function StatusPill({ status }: { status: string }) {
	return (
		<span
			className={cn(
				'px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center justify-center',
				status === 'linked'
					? '!bg-orange-300 !text-orange-700'
					: '!bg-gray-400 !text-gray-600'
			)}
		>
			{status === 'linked' ? 'LINKED' : 'NOT LINKED'}
		</span>
	);
}

function PlatformPill({ platform }: { platform: string }) {
	const colors = {
		twitter: 'bg-purple-300 text-purple-700',
		linkedin: 'bg-indigo-300 text-indigo-700',
		facebook: 'bg-blue-300 text-blue-700',
		threads: 'bg-zinc-300 text-zinc-800',
		reddit: 'bg-orange-300 text-orange-800',
		bluesky: 'bg-sky-300 text-sky-800',
		google_business: 'bg-emerald-300 text-emerald-800',
	};
	const canonicalPlatform = canonicalPodPlatform(platform);

	return (
		<span
			className={cn(
				'px-2 py-1 rounded-full text-xs font-semibold',
				colors[canonicalPlatform as keyof typeof colors] ||
					'bg-gray-300 text-gray-700'
			)}
		>
			{getPodPlatformLabel(platform).toUpperCase()}
		</span>
	);
}

const useProfileDetails = (profileKey: string) =>
	useQuery(api.profiles.getByProfileKey, {
		profileKey,
	});

// Create loader once, outside component
const RANDOM_MESSAGE =
	LOADER_MESSAGES[Math.floor(Math.random() * LOADER_MESSAGES.length)];
const LOADER = <PageLoader message={RANDOM_MESSAGE} />;

// Add these component definitions before the main EngagementPodsAdmin component

type PlatformGroupProps = {
	platform: string;
	allPods?: any[];
	user?: any;
	setSelectedPod: (podId: Id<'engagementPods'> | null) => void;
};

type BroadcastPlatformGroupProps = PlatformGroupProps & {
	profiles?: Profile[];
	getUserInfo: (userId: string) => { email: string; name: string };
	toast: any;
	podButtonDisabled: Record<string, boolean>;
	handleToggleActive: (podId: Id<'engagementPods'>) => Promise<void>;
	handleCreatePod: (platform: string) => Promise<void>;
	setPodToRename: React.Dispatch<
		React.SetStateAction<{ id: Id<'engagementPods'>; name: string } | null>
	>;
	setPodToDelete: React.Dispatch<
		React.SetStateAction<Id<'engagementPods'> | null>
	>;
};

function PlatformHeader({
	platform,
	podsCount,
}: {
	platform: string;
	podsCount: number;
}) {
	return (
		<div className='flex items-center justify-between mb-2 pb-2 border-'>
			<div className='flex items-center gap-2'>
				<div className='w-6 h-6 flex items-center justify-center'>
					{platform === 'twitter' && '𝕏'}
					{platform !== 'twitter' && getPodPlatformIcon(platform)}
				</div>
				<h3 className='font-semibold text-lg'>
					{getPodPlatformLabel(platform)}
				</h3>
			</div>
		</div>
	);
}

function BroadcastPlatformGroup({
	platform,
	profiles,
	allPods,
	user,
	getUserInfo,
	setSelectedPod,
	toast,
	podButtonDisabled,
	handleToggleActive,
	handleCreatePod,
	setPodToRename,
	setPodToDelete,
}: BroadcastPlatformGroupProps) {
	const [isExpanded, setIsExpanded] = useState(false);

	// Filter broadcast pods for this platform, just like SwarmPlatformGroup does
	const getBroadcastPods = () => {
		if (!allPods || !user) return [];

		return allPods.filter(
			(pod) =>
				canonicalPodPlatform(pod.platform) ===
					canonicalPodPlatform(platform) &&
				pod.mode === 'broadcast' &&
				(pod.userId === user.id ||
					pod.members.some(
						(member: any) => member.email === user.email
					))
		);
	};

	const broadcastPods = getBroadcastPods();
	const modeCapability = getPodModeCapability(platform, 'broadcast');

	if (broadcastPods.length === 0) {
		return null;
	}

	// Add this helper function to determine link status
	const getProfileLinkStatus = (pod: any): string => {
		// If the pod has no members, return 'not-linked'
		if (!pod.members || pod.members.length === 0) return 'not-linked';

		// Find the profile associated with this pod
		const member = pod.members[0];
		if (!member) return 'not-linked';

		// Try to find the profile in the profiles list
		const profile = profiles?.find(
			(p: Profile) => p.email.toLowerCase() === member.email.toLowerCase()
		);

		if (!profile) return 'not-linked';

		const podPlatform = canonicalPodPlatform(pod.platform);

		// Check if the profile has any linked status for this platform
		const hasLinkedStatus =
			profile.linkStatus?.status === 'linked' &&
			canonicalPodPlatform(profile.linkStatus?.platform) === podPlatform;

		// Also check platforms array as a fallback
		const hasPlatform = profile.platforms?.some((p: any) => {
			return canonicalPodPlatform(p.platform) === podPlatform;
		});

		// For pods with authenticated profiles, always show as linked
		// This overrides the previous checks if the pod has any activity
		if (
			pod.lastStatus &&
			pod.lastStatus.status &&
			pod.lastStatus.status !== 'idle'
		) {
			return 'linked';
		}

		return hasLinkedStatus || hasPlatform ? 'linked' : 'not-linked';
	};

	return (
		<Card className='mb-4'>
			<CardHeader className='pb-2 pt-4'>
				<div className='flex items-center justify-start'>
					<div
						className='flex items-center justify-center bg-purple-700 text-white rounded-full h-8 w-8 mr-3 cursor-pointer'
						onClick={(e) => {
							e.stopPropagation(); // Stop event from reaching the Card
							setIsExpanded(!isExpanded);
						}}
					>
						{isExpanded ? '🔽' : '▶️'}
					</div>
					<PlatformHeader
						platform={platform}
						podsCount={broadcastPods.length}
					/>
				</div>
			</CardHeader>
			<AnimatePresence>
				{isExpanded && (
					<motion.div
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: 'auto' }}
						exit={{ opacity: 0, height: 0 }}
						transition={{ duration: 0.2 }}
					>
						<CardContent className='pt-4'>
							{!modeCapability.enabled && (
								<div className='mb-4 rounded-md border border-orange-300 bg-orange-50 p-3 text-sm text-orange-900 dark:border-orange-900/60 dark:bg-orange-950/40 dark:text-orange-200'>
									{modeCapability.reason}
								</div>
							)}
							<div className='grid grid-cols-1 gap-4'>
								{broadcastPods.map((pod) => (
									<Card
										key={pod._id}
										className='bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors'
										onClick={() => setSelectedPod(pod._id)}
									>
										<CardHeader className='pb-2'>
											<div className='flex items-center justify-between'>
												<CardTitle className='text-lg whitespace-nowrap'>
													{pod.name}
												</CardTitle>
											</div>
											<div className='flex items-center gap-2'>
												<span className='px-2 py-1 text-xs bg-gradient-to-b from-orange-300 via-orange-500 to-orange-700 text-white rounded-full'>
													BROADCAST
												</span>
												<PlatformPill
													platform={pod.platform}
												/>
											</div>
										</CardHeader>
										<CardContent>
											<div className='flex flex-col gap-4 w-full'>
												{/* Profile Section */}
												<div className='flex items-center gap-2 my-4 text-xl font-semibold'>
													<span className='text-base  text-white'>
														Profile:
													</span>
													<span className='text-pink-600'>
														{pod.members[0]
															?.socialUsername ||
															'N/A'}
													</span>
												</div>

												{/* Status section */}
												<div className='flex items-center gap-2'>
													<StatusPill
														status={getProfileLinkStatus(
															pod
														)}
													/>
													<span className='text-sm text-gray-300'>
														{pod.lastStatus
															?.message ||
															'No recent activity'}
													</span>
												</div>

												{/* Action buttons */}
												<div className='flex items-center justify-between gap-2 mt-2'>
													<Button
														variant='outline'
														size='sm'
														className={cn(
															'w-full transition-all font-medium',
															pod.isActive
																? 'bg-gradient-to-b from-red-300 via-red-500 to-red-700 text-white border-red-400 hover:opacity-90'
																: 'bg-gradient-to-b from-green-300 via-green-500 to-green-700 text-white border-green-400 hover:opacity-90'
														)}
														disabled={
															podButtonDisabled[
																pod._id
															] ||
															!modeCapability.enabled
														}
														onClick={(e) => {
															e.stopPropagation();
															handleToggleActive(
																pod._id
															);
														}}
													>
														{pod.isActive ? (
															<>🔴 Deactivate</>
														) : (
															<>🟢 Activate</>
														)}
													</Button>

													<Button
														variant='outline'
														size='sm'
														className='w-full bg-gradient-to-b from-blue-300 via-blue-500 to-blue-700 text-white border-blue-400 transition-all font-medium hover:opacity-90'
														onClick={(e) => {
															e.stopPropagation();
															setPodToRename({
																id: pod._id,
																name: pod.name,
															});
														}}
													>
														✏️ Rename
													</Button>

													<Button
														variant='outline'
														size='sm'
														className='w-full bg-gradient-to-b from-purple-300 via-purple-500 to-purple-700 text-white border-purple-400 transition-all font-medium hover:opacity-90'
														onClick={(e) => {
															e.stopPropagation();
															setPodToDelete(
																pod._id
															);
														}}
													>
														🗑️ Delete
													</Button>
												</div>
											</div>
										</CardContent>
									</Card>
								))}
							</div>
						</CardContent>
					</motion.div>
				)}
			</AnimatePresence>
		</Card>
	);
}

function SwarmPlatformGroup({
	platform,
	allPods,
	user,
	setSelectedPod,
	podButtonDisabled,
	handleToggleActive,
	setPodToRename,
	setPodToDelete,
}: PlatformGroupProps & {
	podButtonDisabled?: Record<string, boolean>;
	handleToggleActive?: (podId: Id<'engagementPods'>) => Promise<void>;
	setPodToRename?: React.Dispatch<
		React.SetStateAction<{ id: Id<'engagementPods'>; name: string } | null>
	>;
	setPodToDelete?: React.Dispatch<
		React.SetStateAction<Id<'engagementPods'> | null>
	>;
}) {
	const [isExpanded, setIsExpanded] = useState(false);

	// Filter swarm pods for this platform
	const getSwarmPods = () => {
		if (!allPods || !user) return [];

		return allPods.filter(
			(pod) =>
				canonicalPodPlatform(pod.platform) ===
					canonicalPodPlatform(platform) &&
				pod.mode === 'swarm' &&
				(pod.userId === user.id ||
					pod.members.some(
						(member: any) => member.email === user.email
					))
		);
	};

	const swarmPods = getSwarmPods();
	const modeCapability = getPodModeCapability(platform, 'swarm');

	if (swarmPods.length === 0) {
		return null;
	}

	return (
		<Card className='mb-4'>
			<CardHeader className='pb-2 pt-4'>
				<div className='flex items-center justify-start'>
					<div
						className='flex items-center justify-center bg-purple-700 text-white rounded-full h-8 w-8 mr-3 cursor-pointer'
						onClick={(e) => {
							e.stopPropagation(); // Stop event from reaching the Card
							setIsExpanded(!isExpanded);
						}}
					>
						{isExpanded ? '🔽' : '▶️'}
					</div>
					<PlatformHeader
						platform={platform}
						podsCount={swarmPods.length}
					/>
				</div>
			</CardHeader>
			<AnimatePresence>
				{isExpanded && (
					<motion.div
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: 'auto' }}
						exit={{ opacity: 0, height: 0 }}
						transition={{ duration: 0.2 }}
					>
						<CardContent className='pt-4'>
							{!modeCapability.enabled && (
								<div className='mb-4 rounded-md border border-orange-300 bg-orange-50 p-3 text-sm text-orange-900 dark:border-orange-900/60 dark:bg-orange-950/40 dark:text-orange-200'>
									{modeCapability.reason}
								</div>
							)}
							<div className='grid grid-cols-1 gap-4'>
								{swarmPods.map((pod) => (
									<Card
										key={pod._id}
										className='bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors'
										onClick={() => setSelectedPod(pod._id)}
									>
										<CardHeader className='pb-2'>
											<div className='flex items-center justify-between'>
												<CardTitle className='text-lg whitespace-nowrap'>
													{pod.name}
												</CardTitle>
											</div>
											<div className='flex items-center gap-2'>
												<span className='px-2 py-1 text-xs bg-gradient-to-b from-yellow-300 via-yellow-500 to-yellow-700 text-white rounded-full'>
													SWARM
												</span>
												<PlatformPill
													platform={pod.platform}
												/>
											</div>
										</CardHeader>
										<CardContent>
											<div className='space-y-3'>
												<div className='flex items-center gap-1 my-4'>
													<span className='text-lg text-muted-foreground'>
														Members:
													</span>
													<span className='font-semibold'>
														{pod.members.length}
													</span>
												</div>

												{/* Status section */}
												{pod.lastStatus && (
													<div className='flex items-center gap-2'>
														<StatusPill
															status={
																pod.lastStatus
																	?.status ||
																'idle'
															}
														/>
														<span className='text-xs text-gray-500'>
															{pod.lastStatus
																?.message ||
																'No recent activity'}
														</span>
													</div>
												)}

												{/* Action buttons */}
												{handleToggleActive &&
													setPodToRename &&
													setPodToDelete && (
														<div className='flex items-center justify-between gap-2 mt-2'>
															<Button
																variant='outline'
																size='sm'
																className={cn(
																	'w-full transition-all font-medium',
																	pod.isActive
																		? 'bg-gradient-to-b from-red-300 via-red-500 to-red-700 text-white border-red-400 hover:opacity-90'
																		: 'bg-gradient-to-b from-green-300 via-green-500 to-green-700 text-white border-green-400 hover:opacity-90'
																)}
																disabled={
																	podButtonDisabled?.[
																		pod._id
																	] ||
																	!modeCapability.enabled
																}
																onClick={(
																	e
																) => {
																	e.stopPropagation();
																	handleToggleActive(
																		pod._id
																	);
																}}
															>
																{pod.isActive ? (
																	<>
																		🔴
																		Deactivate
																	</>
																) : (
																	<>
																		🟢
																		Activate
																	</>
																)}
															</Button>

															<Button
																variant='outline'
																size='sm'
																className='w-full bg-gradient-to-b from-blue-300 via-blue-500 to-blue-700 text-white border-blue-400 transition-all font-medium hover:opacity-90'
																onClick={(
																	e
																) => {
																	e.stopPropagation();
																	setPodToRename(
																		{
																			id: pod._id,
																			name: pod.name,
																		}
																	);
																}}
															>
																✏️ Rename
															</Button>

															<Button
																variant='outline'
																size='sm'
																className='w-full bg-gradient-to-b from-purple-300 via-purple-500 to-purple-700 text-white border-purple-400 transition-all font-medium hover:opacity-90'
																onClick={(
																	e
																) => {
																	e.stopPropagation();
																	setPodToDelete(
																		pod._id
																	);
																}}
															>
																🗑️ Delete
															</Button>
														</div>
													)}
											</div>
										</CardContent>
									</Card>
								))}
							</div>
						</CardContent>
					</motion.div>
				)}
			</AnimatePresence>
		</Card>
	);
}

// Get other user pods for display
const getGroupedOtherPods = (
	allPods: any[] | undefined,
	user: any,
	getUserInfo: (userId: string) => { email: string; name: string }
) => {
	if (!allPods || !user?.isSuperAdmin) return {};

	// Filter pods not owned by the current user
	const otherPods = allPods.filter(
		(pod) =>
			pod.userId !== user.id &&
			!pod.members.some((member: any) => member.email === user.email)
	);

	// Group by user
	return otherPods.reduce(
		(acc, pod) => {
			const creator = getUserInfo(pod.userId as string);
			const key = creator.email;
			if (!acc[key]) {
				acc[key] = {
					name: creator.name,
					email: creator.email,
					pods: [],
				};
			}
			acc[key].pods.push(pod);
			return acc;
		},
		{} as Record<string, UserPodData>
	);
};

export function EngagementPodsAdmin() {
	const navigate = useNavigate();
	const location = useLocation();

	// Context hooks first
	const { isAdmin, user, isSuperAdmin } = useAuth();
	const { toast } = useToast();
	const { triggerConfetti, Confetti } = useConfetti();
	const { selectedPod, setSelectedPod } = useSelectedPodStore();
	const selectedPodFromState = location.state?.selectedPod;

	// State hooks
	const [newMember, setNewMember] = useState<PodMember>({
		email: '',
		socialUsername: '',
		uploadPostProfileKey: '',
	});
	const [podToDelete, setPodToDelete] = useState<Id<'engagementPods'> | null>(
		null
	);
	const [newPodName, setNewPodName] = useState('');
	const [podToRename, setPodToRename] = useState<{
		id: Id<'engagementPods'>;
		name: string;
	} | null>(null);
	const [podButtonDisabled, setPodButtonDisabled] = useState<
		Record<string, boolean>
	>({});
	const [profileButtonDisabled, setProfileButtonDisabled] = useState<
		Record<string, boolean>
	>({});
	const [memberToRemove, setMemberToRemove] = useState<{
		podId: Id<'engagementPods'>;
		email: string;
	} | null>(null);
	const [podToStart, setPodToStart] = useState<{
		podId: Id<'engagementPods'>;
		urls: { url: string; platform: string }[];
	} | null>(null);
	const [runningPods, setRunningPods] = useState<Record<string, boolean>>({});
	const [urlButtonDisabled, setUrlButtonDisabled] = useState<
		Record<string, boolean>
	>({});
	const [isLoading, setIsLoading] = useState(true);
	const [facebookUrl, setFacebookUrl] = useState('');
	const [twitterUrl, setTwitterUrl] = useState('');
	const [linkedinUrl, setLinkedinUrl] = useState('');
	const [linkedinUsername, setLinkedinUsername] = useState('');
	const [facebookUsername, setFacebookUsername] = useState('');
	const [expandedCreators, setExpandedCreators] = React.useState<Set<string>>(
		new Set()
	);
	const [podMode, setPodMode] = useState<PodMode>('broadcast');
	const [errors, setErrors] = useState<
		Array<{ message: string; timestamp: number }>
	>([]);
	const [newPlatform, setNewPlatform] = useState<Platform | null>(null);
	const platformOptions = useMemo(
		() => getPodPlatformOptions(podMode, { includeDisabled: true }),
		[podMode]
	);
	const selectedPlatform = (newPlatform ||
		getDefaultPodPlatform(podMode)) as Platform;
	const selectedPlatformCapability = getPodModeCapability(
		selectedPlatform,
		podMode
	);

	useEffect(() => {
		if (!isPodModeSupported(selectedPlatform, podMode)) {
			setNewPlatform(getDefaultPodPlatform(podMode));
		}
	}, [podMode, selectedPlatform]);

	// Add an effect to handle loading state
	useEffect(() => {
		if (user?.id) {
			setIsLoading(false);
		}
	}, [user?.id]);

	// Memoize query arguments with strict validation
	const queryArgs = useMemo(() => {
		if (!user?.id) return 'skip';

		return {
			userId: null,
			requestingUserId: user.id,
		};
	}, [user?.id]);

	// Query hooks with strict validation
	const allPods = useQuery(api.engagementPods.list, {
		userId: null,
		requestingUserId: user?.id as unknown as Id<'users'>,
	});
	const users = useQuery(
		api.users.list,
		!isLoading && user?.id && user?.initialApiKey
			? {
					requestingUserId: user.id as unknown as string,
					apiKey: user.initialApiKey ?? '',
				}
			: 'skip'
	);
	const profiles = useQuery(api.profiles.list, {
		userId: user?.id as unknown as Id<'users'>,
		isSuperAdmin: user?.isSuperAdmin || false,
	});
	const profileGroups = useQuery(api.profileGroups.list, {
		userId: user?.id as Id<'users'> | undefined,
		isSuperAdmin: user?.isSuperAdmin || false,
	});
	const podUrls = useQuery(
		api.urls.list,
		!selectedPod
			? 'skip'
			: {
					podId: selectedPod,
					profileKey: null,
				}
	);
	const profileDetails = useQuery(
		api.profiles.getByProfileKey,
		newMember.uploadPostProfileKey
			? { profileKey: newMember.uploadPostProfileKey }
			: 'skip'
	);
	const schedules = useQuery(api.schedules.getByTargetId, {
		targetId: null, // Pass null to get all schedules
	});

	// Mutation hooks
	const toggleUrlActive = useMutation(api.urls.toggleUrlActive);
	const archiveUrl = useMutation(api.urls.archiveUrl);
	const createPod = useMutation(api.engagementPods.create);
	const deletePod = useMutation(api.engagementPods.deletePod);
	const addMember = useMutation(api.engagementPods.addMember);
	const togglePodActive = useMutation(api.engagementPods.togglePodActive);
	const removeMember = useMutation(api.engagementPods.removeMember);
	const renamePod = useMutation(api.engagementPods.renamePod);
	const toggleActive = useMutation(api.profiles.toggleActive);
	const updateStatus = useMutation(api.engagementPods.updateStatus);
	const startPod = useAction(api.urls.startPod);
	const createPodHistory = useMutation(api.podHistory.create);
	const updatePodName = useMutation(api.urls.updatePodName);
	const removePodProfile = useMutation(api.urls.removePodProfile);
	const activatePod = useMutation(api.urls.activatePod);
	const deactivatePod = useMutation(api.urls.deactivatePod);
	const addUrl = useMutation(api.urls.addUrl);
	const getProfileUrls = useQuery(api.urls.list, {
		podId: null,
		profileKey: newMember.uploadPostProfileKey || null,
	});
	const updateUrl = useMutation(api.urls.updateUrl);

	// Memoized values
	const profileInfo = useMemo(
		() => (newMember.uploadPostProfileKey ? profileDetails : undefined),
		[newMember.uploadPostProfileKey, profileDetails]
	);

	const getUserInfo = (userId: string) => {
		const user = (users as any)?.find(
			(u: { _id: Id<'users'>; email: string; name: string }) =>
				(u._id as unknown as string) === userId
		);
		return {
			email: user?.email || 'Unknown',
			name: user?.name || 'Unknown User',
		};
	};

	const [isCreating, setIsCreating] = React.useState(false);
	const [isAddingMember, setIsAddingMember] = useState(false);

	const handleCreatePod = async (platform: string) => {
		try {
			setIsCreating(true);
			if (!user?.id) return;
			if (!newPodName.trim()) {
				throw new Error('Pod name is required');
			}
			const capability = getPodModeCapability(platform, podMode);
			if (!capability.enabled) {
				throw new Error(
					capability.reason ||
						`${getPodPlatformLabel(platform)} ${podMode} pods are disabled for this spec`
				);
			}

			// Count existing pods by mode
			const userPods =
				(allPods as any)?.filter(
					(pod: Pod) =>
						(pod.userId as unknown as string) ===
						(user.id as unknown as string)
				) || [];
			const swarmPodCount = userPods.filter(
				(pod: Pod) => pod.mode === 'swarm' || !pod.mode
			).length;
			const broadcastPodCount = userPods.filter(
				(pod: Pod) => pod.mode === 'broadcast'
			).length;

			// Check limits
			if (podMode === 'swarm' && swarmPodCount >= 6) {
				throw new Error(
					'🐝 Maximum Swarm Pods Reached! You can only create up to 6 Swarm pods 🚫'
				);
			}
			if (podMode === 'broadcast' && broadcastPodCount >= 10) {
				throw new Error(
					'📢 Maximum Broadcast Pods Reached! You can only create up to 10 Broadcast pods 🚫'
				);
			}

			const newPod = await createPod({
				platform: canonicalPodPlatform(platform) as Platform,
				userId: user.id as unknown as Id<'users'>,
				members: [],
				name: newPodName,
				mode: podMode,
			});

			setNewPodName('');
			triggerConfetti();
			toast({
				title: '✨ Pod Created Successfully! 🎉',
				description: `New ${platform} ${podMode === 'broadcast' ? '📢' : '🐝'} ${podMode} pod created successfully! 🚀`,
				duration: 3000,
			});
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : 'Failed to create pod';
			setErrors((prev) => [
				...prev,
				{ message: errorMessage, timestamp: Date.now() },
			]);
			toast({
				title: '❌ Error',
				description: errorMessage,
				variant: 'destructive',
				duration: 3000,
			});
		} finally {
			setIsCreating(false);
		}
	};

	const handleAddMember = async (
		podId: Id<'engagementPods'>,
		member: PodMember,
		triggerConfettiEffect = true
	) => {
		try {
			const pod = allPods?.find(
				(p: { _id: Id<'engagementPods'> }) => p._id === podId
			);
			if (!pod) throw new Error('Pod not found');
			const capability = getPodModeCapability(
				pod.platform,
				pod.mode || 'swarm'
			);
			if (!capability.enabled) {
				throw new Error(
					capability.reason ||
						`${getPodPlatformLabel(pod.platform)} ${pod.mode || 'swarm'} pods are disabled for this spec`
				);
			}

			console.log('Adding member:', member);
			console.log('Available profiles:', profiles);

			const profile = profiles?.find(
				(p: any) => p.email.toLowerCase() === member.email.toLowerCase()
			);

			if (!profile) {
				console.error('Profile lookup failed:', {
					memberEmail: member.email,
					availableEmails: profiles?.map((p: any) => p.email),
				});
				throw new Error(`Profile not found for email: ${member.email}`);
			}

			// Check if profile is linked to the correct platform
			if (!isProfileLinkedToPlatform(profile, pod.platform)) {
				throw new Error(
					`Profile must be linked to ${pod.platform} before it can be added to this pod`
				);
			}

			const memberToAdd = {
				email: member.email,
				socialUsername: member.socialUsername,
				uploadPostProfileKey: member.uploadPostProfileKey,
			};

			await addMember({
				podId,
				member: memberToAdd,
			});

			// If this is a broadcast mode pod, assign existing URLs to it
			if (pod.mode === 'broadcast') {
				const existingUrls = getProfileUrls || [];
				const existingPodUrls = podUrls || [];

				// Filter out URLs that already exist in the pod
				const newUrls = existingUrls.filter(
					(url: any) =>
						!existingPodUrls.some(
							(podUrl: any) =>
								podUrl.url === url.url &&
								podUrl.platform === url.platform
						)
				);

				// Update existing URLs to associate them with the pod
				for (const url of newUrls) {
					await updateUrl({
						urlId: url._id,
						podId: podId,
						isActive: true,
						mode: 'broadcast',
					});
				}

				if (newUrls.length > 0) {
					toast({
						title: '🔄 URLs Assigned',
						description: `${newUrls.length} existing URLs assigned to pod`,
						duration: 3000,
					});
				}
			}

			if (triggerConfettiEffect) {
				triggerConfetti();
			}

			toast({
				title: '✨ Member Added ✨',
				description: 'Successfully added member to pod',
				duration: 3000,
			});
		} catch (error) {
			console.error('Add member error:', error);
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to add member',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleToggleActive = async (podId: Id<'engagementPods'>) => {
		try {
			// Disable the button
			setPodButtonDisabled((prev) => ({ ...prev, [podId]: true }));

			const pod = allPods?.find(
				(p: { _id: Id<'engagementPods'> }) => p._id === podId
			);
			if (!pod) throw new Error('Pod not found');

			await togglePodActive({ podId });

			// Enable the button after 5 seconds
			setTimeout(() => {
				setPodButtonDisabled((prev) => ({ ...prev, [podId]: false }));
			}, 5000);

			toast({
				title: '✨ Pod Updated ✨',
				description: 'Pod status updated successfully.',
				duration: 3000,
			});
		} catch (error) {
			// Enable the button immediately if there's an error
			setPodButtonDisabled((prev) => ({ ...prev, [podId]: false }));
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to update pod status',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleDeletePod = async (podId: Id<'engagementPods'>) => {
		try {
			// Get the current pod data before deletion
			const pod = allPods?.find((p: any) => p._id === podId);
			if (!pod) throw new Error('Pod not found');

			// Delete the pod from database
			await deletePod({ podId });

			toast({
				title: '️ Pod Deleted',
				description: 'Pod has been deleted successfully.',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to delete pod',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleRemoveMember = async (
		podId: Id<'engagementPods'>,
		memberEmail: string
	) => {
		try {
			const pod = allPods?.find(
				(p: { _id: Id<'engagementPods'> }) => p._id === podId
			);
			if (!pod) throw new Error('Pod not found');

			await removeMember({
				podId,
				memberEmail,
			});

			toast({
				title: '✨ Profile Removed ✨',
				description: 'Profile removed from pod successfully.',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to remove member',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleRenamePod = async (
		podId: Id<'engagementPods'>,
		name: string
	) => {
		try {
			await updatePodName({
				podId,
				newName: name,
			});

			setPodToRename(null);
			toast({
				title: '✨ Pod Renamed ✨',
				description: 'Pod name updated successfully.',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to rename pod',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleToggleProfileActive = async (profile: Profile, pod: Pod) => {
		try {
			setProfileButtonDisabled((prev) => ({
				...prev,
				[profile._id]: true,
			}));

			await toggleActive({ profileId: profile._id });

			// Enable button after delay
			setTimeout(() => {
				setProfileButtonDisabled((prev) => ({
					...prev,
					[profile._id]: false,
				}));
			}, 5000);

			toast({
				title: '✨ Profile Updated ✨',
				description: 'Profile status updated successfully.',
				duration: 3000,
			});
		} catch (error) {
			setProfileButtonDisabled((prev) => ({
				...prev,
				[profile._id]: false,
			}));
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to update profile status',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const fetchPodUrls = (podId: Id<'engagementPods'>) => {
		return (
			podUrls?.filter(
				(url: any) => url.podId === podId && url.isActive
			) || []
		);
	};

	// Effect hooks
	useEffect(() => {
		if (Array.isArray(allPods)) {
			allPods.forEach((pod) => {
				const isPodRunning = (status?: string) => {
					const runningStatuses = [
						'POD STARTING',
						'INITIALIZING',
						'🔨 SCRAPING POST',
						'🔌 SCRAPING DONE',
						'✍️ Commenting...',
						'✍️ Replying...',
						'✅ COMMENTED',
						'👀 Logging...',
						'😴 Sleeping...',
					];
					return status
						? runningStatuses.includes(status.trim())
						: false;
				};

				if (
					pod.lastStatus?.status === '⚡️ ENGAGEMENT POD DONE' ||
					pod.lastStatus?.message?.toUpperCase() ===
						'NO CURRENT ACTIVITY' ||
					!isPodRunning(pod.lastStatus?.status)
				) {
					setRunningPods((prev) => ({ ...prev, [pod._id]: false }));
				} else if (pod.lastStatus?.status) {
					setRunningPods((prev) => ({ ...prev, [pod._id]: true }));
				}
			});
		}
	}, [allPods]);

	useEffect(() => {
		const handlePopState = () => {
			setSelectedPod(null);
			if (
				location.pathname === '/admin/pods' &&
				location.state?.fromHistory
			) {
				navigate('/admin/pods', { replace: true, state: undefined });
			}
		};
		window.addEventListener('popstate', handlePopState);
		return () => {
			window.removeEventListener('popstate', handlePopState);
		};
	}, [navigate, location, setSelectedPod]);

	useEffect(() => {
		if (selectedPod && allPods) {
			const pod = allPods.find(
				(p: Doc<'engagementPods'>) => p._id === selectedPod
			);
			if (!pod) {
				setSelectedPod(null);
			}
		}
	}, [selectedPod, allPods, setSelectedPod]);

	const handlePodSelect = (podId: Id<'engagementPods'>) => {
		setSelectedPod(podId);
	};

	const handleAddProfileGroup = async (
		podId: Id<'engagementPods'>,
		groupId: Id<'profileGroups'>
	) => {
		try {
			const group = profileGroups?.find((g: any) => g._id === groupId);
			if (!group) throw new Error('Profile group not found');

			const groupProfiles = group.profileIds
				.map((id: Id<'profiles'>) =>
					profiles?.find((p: any) => p._id === id)
				)
				.filter(Boolean)
				.filter(
					(
						profile: Doc<'profiles'> | undefined
					): profile is Doc<'profiles'> =>
						!isBlockedProfile(profile?.profileKey ?? '')
				);

			if (groupProfiles.length === 0) {
				throw new Error('No valid profiles found in group');
			}

			const currentPod = allPods?.find((p: any) => p._id === podId);
			if (!currentPod) throw new Error('Pod not found');

			let addedCount = 0;
			let errorCount = 0;

			const batchSize = 3;
			for (let i = 0; i < groupProfiles.length; i += batchSize) {
				const batch = groupProfiles.slice(i, i + batchSize);
				await Promise.all(
					batch.map(async (profile: Doc<'profiles'>) => {
						try {
							if (!profile) return;

							if (
								currentPod.members.some(
									(m: PodMember) => m.email === profile.email
								)
							) {
								return;
							}

							const isLinked = profile.linkStatuses?.some(
								(status: LinkStatusValue) =>
									status.status === 'linked' &&
									status.platform.toLowerCase() ===
										currentPod.platform.toLowerCase()
							);

							if (!isLinked) return;

							const member = {
								email: profile.email,
								socialUsername: profile.profileTitle,
								uploadPostProfileKey: profile.profileKey,
							};

							// Only proceed if all required fields are present
							if (
								!member.email ||
								!member.socialUsername ||
								!member.uploadPostProfileKey
							) {
								console.error(
									`Missing required fields for profile ${profile.email}`
								);
								errorCount++;
								return;
							}

							// Validate member before adding
							const validationError = validatePodMember(member);
							if (validationError) {
								console.error(
									`Validation error for ${profile.email}: ${validationError}`
								);
								errorCount++;
								return;
							}

							try {
								// Add the member to the pod
								await handleAddMember(podId, member, false);

								addedCount++;
							} catch (error) {
								console.error(
									`Failed to process profile ${profile?.email || 'unknown'}:`,
									error
								);
								errorCount++;
							}
						} catch (error) {
							console.error(
								`Failed to process profile ${profile?.email || 'unknown'}:`,
								error
							);
						}
					})
				);

				// Add a small delay between batches
				if (i + batchSize < groupProfiles.length) {
					await new Promise((resolve) => setTimeout(resolve, 1000));
				}
			}

			triggerConfetti();
			toast({
				title: '✨ Profile Group Added ✨',
				description: `Successfully added ${addedCount} profiles${
					errorCount > 0 ? ` (${errorCount} errors)` : ''
				}`,
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to add profile group',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleBulkRemoveMembers = async (
		podId: Id<'engagementPods'>,
		emails: string[],
		onProgress?: (current: number, email: string) => void
	) => {
		try {
			const currentPod = allPods?.find((p: any) => p._id === podId);
			if (!currentPod) throw new Error('Pod not found');

			let successCount = 0;
			let errorCount = 0;

			// Process removals in batches
			const batchSize = 3;
			for (let i = 0; i < emails.length; i += batchSize) {
				const batch = emails.slice(i, i + batchSize);
				await Promise.all(
					batch.map(async (email, batchIndex) => {
						try {
							// Get the member being removed
							const member = currentPod.members.find(
								(m: PodMember) => m.email === email
							);

							// Skip if member not found instead of throwing error
							if (!member) {
								console.log(
									`Member ${email} not found in pod, skipping...`
								);
								return;
							}

							// Remove the member
							await handleRemoveMember(podId, email);
							onProgress?.(i + batchIndex + 1, email);
							successCount++;
						} catch (error) {
							console.error(
								`Failed to remove profile ${email}:`,
								error
							);
							errorCount++;
						}
					})
				);

				// Add a small delay between batches
				if (i + batchSize < emails.length) {
					await new Promise((resolve) => setTimeout(resolve, 1000));
				}
			}

			// Show final toast with results
			if (successCount > 0) {
				toast({
					title: '✨ Members Removed ✨',
					description: `Successfully removed ${successCount} members${
						errorCount > 0 ? ` (${errorCount} failed)` : ''
					}`,
					duration: 3000,
				});
			} else if (errorCount > 0) {
				toast({
					title: 'Warning',
					description: `Failed to remove ${errorCount} members`,
					variant: 'destructive',
					duration: 3000,
				});
			}
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to remove members',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleStartPod = async (podId: Id<'engagementPods'>) => {
		let pod;
		try {
			pod = allPods?.find((p: any) => p._id === podId);
			if (!pod) throw new Error('Pod not found');

			// Filter out blocked profiles
			const validMembers = pod.members.filter(
				(member: PodMember) =>
					!isBlockedProfile(member.uploadPostProfileKey)
			);

			// Update pod with filtered members
			pod.members = validMembers;

			if (!pod.members || pod.members.length === 0) {
				toast({
					title: '⚠️ Cannot Start Pod',
					description: 'No valid members found in the pod.',
					variant: 'destructive',
					duration: 3000,
				});
				return;
			}

			// Check if there are any active URLs
			const activeUrls = fetchPodUrls(podId).filter(
				(url: any) => url.isActive
			);
			if (activeUrls.length === 0) {
				toast({
					title: 'No Active URLs',
					description:
						'Please add and activate some URLs before starting the pod.',
					variant: 'destructive',
					duration: 3000,
				});
				return;
			}

			setRunningPods((prev) => ({ ...prev, [podId]: true }));

			// Update pod status to POD STARTING immediately
			await updateStatus({
				podId,
				status: 'POD STARTING',
				message: 'Initializing pod engagement process',
				timestamp: new Date().toISOString(),
			});

			await startPod({ podId });
			triggerConfetti();
			toast({
				title: '✨ Pod Started ✨',
				description: 'Successfully started processing pod URLs',
				duration: 3000,
			});
		} catch (error) {
			setRunningPods((prev) => ({ ...prev, [podId]: false }));
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to start pod',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleToggleUrlActive = async (urlId: Id<'urls'>) => {
		setUrlButtonDisabled((prev) => ({ ...prev, [urlId]: true }));
		await toggleUrlActive({ urlId });
		setTimeout(
			() => setUrlButtonDisabled((prev) => ({ ...prev, [urlId]: false })),
			5000
		);
	};

	const handleArchiveUrl = async (urlId: Id<'urls'>) => {
		await archiveUrl({ urlId });
	};

	const handlers = {
		handleToggleActive,
		handleDeletePod,
		handleAddMember,
		handleToggleProfileActive,
		handleRemoveMember,
		podButtonDisabled,
		profileButtonDisabled,
		runningPods,
		newMember,
		setNewMember,
		profiles,
		setPodToStart,
		fetchPodUrls,
		handleRenamePod: (podId: Id<'engagementPods'>, name: string) => {
			setPodToRename({ id: podId, name });
		},
		profileGroups: (profileGroups as any) || [],
		handleAddProfileGroup,
		handleBulkRemoveMembers,
		handleStartPod,
		urls: podUrls?.filter((url: any) => url.podId === selectedPod) || [],
		handleToggleUrlActive,
		handleArchiveUrl,
		urlButtonDisabled,
		triggerConfetti,
	};

	useEffect(() => {
		console.log('selectedPod changed:', selectedPod);
		console.log('allPods available:', allPods?.length);
	}, [selectedPod, allPods]);

	const isProfileLinkedToPlatform = (profile: any, platform: string) => {
		const targetPlatform = canonicalPodPlatform(platform);

		// Check linkStatuses array
		const hasLinkedStatus = profile.linkStatuses?.some((status: any) => {
			return (
				status.status === 'linked' &&
				canonicalPodPlatform(status.platform) === targetPlatform
			);
		});

		// Also check platforms array
		const hasTwitterPlatform = profile.platforms?.some((p: any) => {
			return canonicalPodPlatform(p.platform) === targetPlatform;
		});

		return hasLinkedStatus || hasTwitterPlatform;
	};

	// Update the loading check to be more comprehensive
	const isFullyLoaded =
		!isLoading &&
		user?.id &&
		allPods !== undefined &&
		users !== undefined &&
		profiles !== undefined;

	if (!isFullyLoaded) {
		return LOADER;
	}

	return (
		<div className='container mx-auto p-6 pb-80'>
			<div className='flex justify-between items-center mb-6'>
				<div>
					<h1 className='text-3xl font-bold'>Engagement Pods</h1>
					<p className='text-muted-foreground my-4'>
						Create and manage your engagement pods.
					</p>
				</div>
				<Button
					variant='outline'
					asChild
					className='bg-purple-600 hover:bg-purple-700 text-white'
				>
					<Link
						to='/admin/pods/history'
						className='flex items-center gap-2'
					>
						📋 View Pod History
					</Link>
				</Button>
			</div>

			{selectedPod && allPods ? (
				<div className='grid gap-6'>
					{(() => {
						// Find the pod in allPods
						const foundPod = allPods.find(
							(p: any) => p._id === selectedPod
						);

						if (foundPod) {
							return (
								<PodExpandedView
									pod={foundPod as any}
									typedProfiles={profiles as any}
									onClose={() => setSelectedPod(null)}
									setRunningPods={setRunningPods}
									{...(handlers as any)}
								/>
							);
						} else {
							// If we can't find the pod, we should redirect back to the list
							console.error(
								`Pod with ID ${selectedPod} not found in allPods`
							);

							// Immediately redirect back to the list
							setSelectedPod(null);

							return (
								<Card className='mb-6'>
									<CardHeader>
										<CardTitle>Redirecting...</CardTitle>
									</CardHeader>
									<CardContent>
										<p>Returning to pods list...</p>
									</CardContent>
								</Card>
							);
						}
					})()}
				</div>
			) : (
				<>
					{/* User's Pods Section - Show once at the top */}
					<Card className='mb-12 glow-card'>
						<CardHeader>
							<div className='space-y-2'>
								<div className='flex items-center gap-2'>
									<span className='text-lg font-semibold uppercase text-foreground'>
										MY PODS
									</span>
									<span className='text-2xl font-bold text-pink-500 uppercase'>
										{
											getUserInfo(
												user?.id as unknown as string
											).name
										}
									</span>
								</div>
								<p className='text-sm text-muted-foreground'>
									{(() => {
										// Count real pods created by the user
										const realPodCount =
											allPods?.reduce(
												(count: any, pod: any) =>
													count +
													((pod.userId as any) ===
													(user?.id as any)
														? 1
														: 0),
												0
											) || 0;

										// Count unique connected social profiles as virtual pods
										return realPodCount;
									})()}{' '}
									pods • {user?.email}
								</p>
							</div>
						</CardHeader>
					</Card>

					{/* Show placeholder if no pods exist */}
					{(() => {
						// Count pods created by the user
						const userPodCount =
							allPods?.filter(
								(pod: any) =>
									(pod.userId as unknown as string) ===
									(user?.id as unknown as string)
							).length || 0;

						if (userPodCount === 0) {
							return (
								<Card className='mb-6 border-dashed border-2 border-gray-300'>
									<CardContent className='flex flex-col items-center justify-center py-8'>
										<div className='text-5xl mb-4'>✨</div>
										<h3 className='text-xl font-semibold mb-2'>
											No Engagement Pods Yet
										</h3>
										<p className='text-muted-foreground text-center mb-4'>
											Create your first pod using the form
											below.
										</p>
										<div className='animate-bounce'>⬇️</div>
									</CardContent>
								</Card>
							);
						}

						return null;
					})()}

					{/* Group pods by mode first, then by platform */}
					<div className='space-y-8 mb-12'>
						{/* BROADCAST MODE SECTION */}
						<div className='space-y-4'>
							<h2 className='text-3xl font-bold text-orange-500'>
								📢 BROADCAST MODE
							</h2>

							{/* Placeholder for no Broadcast mode pods */}
							{(() => {
								// Check if user has any broadcast mode pods
								const hasBroadcastPods = allPods?.some(
									(pod: any) =>
										(pod.userId as unknown as string) ===
											(user?.id as unknown as string) &&
										pod.mode === 'broadcast'
								);

								if (!hasBroadcastPods) {
									return (
										<Card className='mb-4 border-dashed border-2 border-orange-300'>
											<CardContent className='flex flex-col items-center justify-center py-6'>
												<div className='text-4xl mb-3'>
													📢
												</div>
												<h3 className='text-lg font-semibold mb-2'>
													No Broadcast Pods
												</h3>
												<p className='text-muted-foreground text-center text-sm'>
													Create a Broadcast pod to
													manage content for a single
													profile.
												</p>
											</CardContent>
										</Card>
									);
								}

								return null;
							})()}

							{POD_PLATFORM_ORDER.map((platform) => (
								<BroadcastPlatformGroup
									key={`broadcast-${platform}`}
									platform={platform}
									profiles={profiles as any}
									allPods={allPods}
									user={user}
									getUserInfo={getUserInfo}
									setSelectedPod={setSelectedPod}
									toast={toast}
									podButtonDisabled={podButtonDisabled}
									handleToggleActive={handleToggleActive}
									handleCreatePod={handleCreatePod}
									setPodToRename={setPodToRename}
									setPodToDelete={setPodToDelete}
								/>
							))}
						</div>

						{/* SWARM MODE SECTION */}
						<div className='space-y-4'>
							<h2 className='text-3xl font-bold text-yellow-500'>
								🐝 SWARM MODE
							</h2>

							{/* Placeholder for no Swarm mode pods */}
							{(() => {
								// Check if user has any swarm mode pods
								const hasSwarmPods = allPods?.some(
									(pod: any) =>
										(pod.userId as unknown as string) ===
											(user?.id as unknown as string) &&
										pod.mode === 'swarm'
								);

								if (!hasSwarmPods) {
									return (
										<Card className='mb-4 border-dashed border-2 border-yellow-300'>
											<CardContent className='flex flex-col items-center justify-center py-6'>
												<div className='text-4xl mb-3'>
													🐝
												</div>
												<h3 className='text-lg font-semibold mb-2'>
													No Swarm Pods
												</h3>
												<p className='text-muted-foreground text-center text-sm'>
													Create a Swarm pod to engage
													with content using multiple
													profiles.
												</p>
											</CardContent>
										</Card>
									);
								}

								return null;
							})()}

							{POD_PLATFORM_ORDER.map((platform) => (
								<SwarmPlatformGroup
									key={`swarm-${platform}`}
									platform={platform}
									allPods={allPods}
									user={user}
									setSelectedPod={setSelectedPod}
									podButtonDisabled={podButtonDisabled}
									handleToggleActive={handleToggleActive}
									setPodToRename={setPodToRename}
									setPodToDelete={setPodToDelete}
								/>
							))}
						</div>
					</div>

					{/* Other Users' Pods Section - Only shown to super admin */}
					{user?.isSuperAdmin && (
						<>
							{/* Calculate other users' pods */}
							{(() => {
								const otherPods = getGroupedOtherPods(
									allPods,
									user,
									getUserInfo
								);
								if (Object.keys(otherPods).length === 0)
									return null;

								return (
									<>
										<div className='relative py-8'>
											<div className='absolute inset-0 flex items-center'>
												<span className='w-full border-t border-gray-600' />
											</div>
											<div className='relative flex justify-center'>
												<span className='bg-background px-6 text-lg font-semibold text-gray-400'>
													OTHER USER PODS
												</span>
											</div>
										</div>

										{/* Render other users' pods */}
										<div className='space-y-6'>
											{(
												Object.entries(otherPods) as [
													string,
													UserPodData,
												][]
											).map(([email, userData]) => (
												<Card key={email}>
													<CardHeader className='pb-6'>
														<div className='space-y-2'>
															<div className='flex items-center gap-2'>
																<motion.button
																	onClick={() =>
																		setExpandedCreators(
																			(
																				prev
																			) => {
																				const next =
																					new Set(
																						prev
																					);
																				if (
																					next.has(
																						email
																					)
																				) {
																					next.delete(
																						email
																					);
																				} else {
																					next.add(
																						email
																					);
																				}
																				return next;
																			}
																		)
																	}
																	className='w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center text-white hover:bg-purple-700 transition-colors'
																	animate={{
																		rotate: expandedCreators.has(
																			email
																		)
																			? 180
																			: 0,
																	}}
																	transition={{
																		duration: 0.2,
																		ease: 'easeInOut',
																	}}
																>
																	{expandedCreators.has(
																		email
																	)
																		? '🔽'
																		: '▶️'}
																</motion.button>
																<span className='text-xl font-semibold uppercase text-foreground'>
																	POD CREATOR:
																</span>
																<span className='text-2xl font-bold text-pink-500 uppercase'>
																	{
																		userData.name
																	}
																</span>
															</div>
															<p className='text-sm text-muted-foreground'>
																{
																	userData
																		.pods
																		.length
																}{' '}
																pod
																{userData.pods
																	.length !==
																1
																	? 's'
																	: ''}{' '}
																• {email}
															</p>
														</div>
													</CardHeader>
													<AnimatePresence>
														{expandedCreators.has(
															email
														) && (
															<motion.div
																initial={{
																	opacity: 0,
																	height: 0,
																}}
																animate={{
																	opacity: 1,
																	height: 'auto',
																}}
																exit={{
																	opacity: 0,
																	height: 0,
																}}
																transition={{
																	duration: 0.2,
																}}
															>
																<CardContent className='pt-2 pb-6 px-6'>
																	<div className='grid gap-4'>
																		{userData.pods.map(
																			(
																				pod: any
																			) => (
																				<Card
																					key={
																						pod._id
																					}
																					className='cursor-pointer hover:shadow-lg transition-shadow p-6'
																					onClick={() => {
																						handlePodSelect(
																							pod._id
																						);
																					}}
																				>
																					<div className='flex flex-col gap-4'>
																						<div className='flex justify-between items-center'>
																							<div className='flex items-center gap-2'>
																								<span
																									className={`${pod.isActive ? 'text-green-500' : 'text-red-500'} font-semibold`}
																								>
																									{pod.isActive
																										? '🟢'
																										: '🔴'}
																								</span>
																								<div>
																									<h3 className='font-semibold'>
																										{
																											pod.name
																										}
																									</h3>
																									<p className='text-sm text-muted-foreground'>
																										{
																											pod
																												.members
																												.length
																										}{' '}
																										members
																									</p>
																								</div>
																							</div>
																							<div className='flex items-center gap-2'>
																								{runningPods[
																									pod
																										._id
																								] && (
																									<span className='animate-spin'>
																										⚡️
																									</span>
																								)}
																							</div>
																						</div>
																						<PodStatus
																							status={
																								pod
																									.lastStatus
																									?.status
																							}
																							message={
																								pod
																									.lastStatus
																									?.message
																							}
																							timestamp={
																								pod
																									.lastStatus
																									?.timestamp
																							}
																							runCount={
																								pod.runCount
																							}
																						/>
																					</div>
																				</Card>
																			)
																		)}
																	</div>
																</CardContent>
															</motion.div>
														)}
													</AnimatePresence>
												</Card>
											))}
										</div>
									</>
								);
							})()}
						</>
					)}
				</>
			)}

			{/* Create Pod Section - Only show when no pod is selected */}
			{!selectedPod && (
				<Card className='glow-card mt-11'>
					<CardHeader>
						<CardTitle className='text-2xl font-bold text-pink-500'>
							Create New Pod
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className='space-y-6'>
							<div className='mb-6'>
								<label className='block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300'>
									Engagement Mode
								</label>
								<SlideSelector
									value={podMode}
									onChange={(value) =>
										setPodMode(value as PodMode)
									}
									options={[
										{
											value: 'broadcast',
											label: 'Broadcast Mode',
											icon: '📢',
											description: 'One Member Per Pod',
											className:
												'bg-gradient-to-b from-orange-300 via-orange-500 to-orange-700',
										},
										{
											value: 'swarm',
											label: 'Swarm Mode',
											icon: '🐝',
											description:
												'All Pod Members Engage',
											className:
												'bg-gradient-to-b from-purple-300 via-purple-500 to-purple-800',
										},
									]}
									className='w-full'
									activeColor={
										podMode === 'broadcast'
											? 'orange'
											: 'purple'
									}
								/>
							</div>

							{/* Replace custom dropdown with shadcn/ui Select component */}
							<div className='space-y-4'>
								<div>
									<label className='block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300'>
										Select Platform
									</label>
									<Select
										value={selectedPlatform}
										onValueChange={(value) =>
											setNewPlatform(value as Platform)
										}
									>
										<SelectTrigger>
											<SelectValue placeholder='Select platform' />
										</SelectTrigger>
										<SelectContent>
											{platformOptions.map((option) => (
												<SelectItem
													key={option.platform}
													value={option.platform}
													disabled={
														!option.modeCapability
															.enabled
													}
												>
													<div className='flex flex-col gap-1'>
														<div className='flex items-center'>
															<span className='mr-2 min-w-6'>
																{option.icon}
															</span>
															<span>
																{option.label}
															</span>
															{!option
																.modeCapability
																.enabled && (
																<span className='ml-2 text-xs uppercase text-orange-500'>
																	disabled
																</span>
															)}
														</div>
														{!option
															.modeCapability
															.enabled && (
															<span className='text-xs text-muted-foreground'>
																{
																	option
																		.modeCapability
																		.reason
																}
															</span>
														)}
													</div>
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									{!selectedPlatformCapability.enabled && (
										<p className='mt-2 text-sm text-orange-500'>
											{
												selectedPlatformCapability.reason
											}
										</p>
									)}
								</div>

								<div className='space-y-4'>
									<Input
										placeholder='Enter pod name'
										value={newPodName}
										onChange={(e) =>
											setNewPodName(e.target.value)
										}
									/>
									<Button
										onClick={() =>
											handleCreatePod(
												selectedPlatform
											)
										}
										disabled={
											isCreating ||
											!newPodName.trim() ||
											!selectedPlatformCapability.enabled
										}
										className={cn(
											'w-full',
											podMode === 'broadcast'
												? 'bg-gradient-to-b from-orange-300 via-orange-500 to-orange-700'
												: 'bg-gradient-to-b from-purple-300 via-purple-500 to-purple-800',
											'text-white hover:opacity-90'
										)}
									>
										Create{' '}
										{podMode === 'broadcast'
											? 'Broadcast'
											: 'Swarm'}{' '}
										Pod
									</Button>
								</div>
							</div>
						</div>
					</CardContent>
				</Card>
			)}
			<ConfirmDialog
				isOpen={memberToRemove !== null}
				onClose={() => setMemberToRemove(null)}
				title='Remove Pod Member'
				description='Are you sure you want to remove this member from the pod? This action cannot be undone.'
				onConfirm={async () => {
					if (memberToRemove) {
						await handleRemoveMember(
							memberToRemove.podId,
							memberToRemove.email
						);
						setMemberToRemove(null);
					}
				}}
			/>
			<ConfirmDialog
				isOpen={podToDelete !== null}
				onClose={() => setPodToDelete(null)}
				title='Delete Pod'
				description='Are you sure you want to delete this pod? This action cannot be undone.'
				onConfirm={async () => {
					if (podToDelete) {
						await handleDeletePod(podToDelete);
						setPodToDelete(null);
					}
				}}
			/>
			<Dialog
				open={podToRename !== null}
				onOpenChange={(open) => !open && setPodToRename(null)}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Rename Pod</DialogTitle>
					</DialogHeader>
					<Input
						placeholder='New pod name'
						value={podToRename?.name ?? ''}
						onChange={(e) =>
							setPodToRename((prev) =>
								prev ? { ...prev, name: e.target.value } : null
							)
						}
					/>
					<DialogFooter>
						<Button
							variant='outline'
							onClick={() => setPodToRename(null)}
						>
							Cancel
						</Button>
						<Button
							onClick={() => {
								if (podToRename) {
									handleRenamePod(
										podToRename.id,
										podToRename.name
									);
								}
							}}
							disabled={!podToRename?.name.trim()}
						>
							Save
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<Dialog
				open={podToStart !== null}
				onOpenChange={(open) => !open && setPodToStart(null)}
			>
				<DialogContent className='max-w-2xl'>
					<DialogHeader>
						<DialogTitle>
							Start{' '}
							{
								allPods?.find(
									(p: any) => p._id === podToStart?.podId
								)?.name
							}{' '}
							Pod
						</DialogTitle>
					</DialogHeader>
					<div className='max-h-[400px] overflow-y-auto space-y-4'>
						<h3 className='font-semibold'>
							The following URLs will be processed:
						</h3>
						{podToStart?.urls.length === 0 ? (
							<p className='text-yellow-600'>
								No Active URLs found for this pod.
								<br />
								Activate some URLs in the{' '}
								<a
									href='/admin/urls'
									className='text-blue-600 underline hover:text-blue-800'
								>
									URLs tab
								</a>
								.
							</p>
						) : (
							<div className='grid gap-4'>
								{podToStart?.urls.map((url, idx) => (
									<div
										key={idx}
										className='flex items-center justify-between p-4 rounded-lg border bg-card text-card-foreground shadow-sm'
									>
										<div className='flex items-center gap-2'>
											<span className='px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800'>
												Active
											</span>
											<span className='text-sm font-medium'>
												{url.url}
											</span>
										</div>
									</div>
								))}
							</div>
						)}
					</div>
					<DialogFooter>
						<Button
							variant='outline'
							onClick={() => setPodToStart(null)}
						>
							Cancel
						</Button>
						<Button
							onClick={async () => {
								if (!podToStart) return;

								// Check if there are any active URLs
								if (podToStart.urls.length === 0) {
									toast({
										title: 'No Active URLs',
										description:
											'Please add and activate some URLs before starting the pod.',
										variant: 'destructive',
										duration: 3000,
									});
									return;
								}

								try {
									setRunningPods((prev) => ({
										...prev,
										[podToStart.podId]: true,
									}));

									// Record pod history
									const currentPod = allPods?.find(
										(p: any) => p._id === podToStart.podId
									);
									if (!currentPod)
										throw new Error('Pod not found');

									await createPodHistory({
										podId: podToStart.podId,
										podName: currentPod.name,
										platform: currentPod.platform,
										timestamp: new Date().toISOString(),
										memberCount: currentPod.members.length,
										urlCount: podToStart.urls.length,
										status: 'POD STARTING',
										statusMessage:
											'Initializing pod engagement process',
										startedBy:
											user?.id as unknown as Id<'users'>,
										createdBy:
											currentPod.userId as unknown as Id<'users'>,
									});

									// Update pod status to POD STARTING immediately

									await updateStatus({
										podId: podToStart.podId,
										status: 'POD STARTING',
										message:
											'Initializing pod engagement process',
										timestamp: new Date().toISOString(),
									});

									await startPod({ podId: podToStart.podId });
									triggerConfetti();
									toast({
										title: '✨ Pod Started ✨',
										description:
											'Successfully started processing pod URLs',
										duration: 3000,
									});
									setPodToStart(null);
								} catch (error) {
									setRunningPods((prev) => ({
										...prev,
										[podToStart.podId]: false,
									}));
									toast({
										title: 'Error',
										description:
											error instanceof Error
												? error.message
												: 'Failed to start pod',
										variant: 'destructive',
										duration: 3000,
									});
								}
							}}
							className='w-full bg-purple-600 hover:bg-purple-700 text-white text-lg font-semibold'
							disabled={runningPods[podToStart?.podId ?? '']}
						>
							{runningPods[podToStart?.podId ?? ''] ? (
								<>
									<span className='animate-spin mr-2'>
										⚡️
									</span>
									🔄 Pod running...
								</>
							) : (
								'✨ Start Pod'
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<div className='fixed inset-0 pointer-events-none z-50'>
				<Confetti />
			</div>
			{errors.length > 0 && (
				<Card className='mt-4 border-red-500'>
					<CardHeader>
						<CardTitle className='text-red-500'>
							Recent Errors
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className='space-y-2'>
							{errors.slice(-5).map((error, index) => (
								<div
									key={error.timestamp}
									className='flex items-center justify-between p-2 bg-red-50 dark:bg-red-900/20 rounded-lg'
								>
									<div className='flex items-center gap-2'>
										<span className='text-red-500'>⚠️</span>
										<span className='text-sm text-red-700 dark:text-red-300'>
											{error.message}
										</span>
									</div>
									<Button
										variant='ghost'
										size='sm'
										onClick={() =>
											setErrors((prev) =>
												prev.filter(
													(_, i) => i !== index
												)
											)
										}
										className='text-red-500 hover:text-red-700'
									>
										✕
									</Button>
								</div>
							))}
							{errors.length > 0 && (
								<Button
									variant='outline'
									size='sm'
									onClick={() => setErrors([])}
									className='w-full mt-2 text-red-500 hover:text-red-700 border-red-200'
								>
									Clear All Errors
								</Button>
							)}
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
